import { TextStyleKit } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import type { Editor, Extensions } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useMemo, useRef, type CSSProperties } from 'react';
import { blockTextToTipTapContent, tipTapHtmlToBlockText } from '../utils/richText';

type EmailBlockTipTapProps = {
  blockId: string;
  content: string;
  blockType: 'text' | 'title';
  style: CSSProperties;
  disabled?: boolean;
  onChange: (html: string) => void;
  onEditorReady?: (editor: Editor | null) => void;
};

function applyEditorStyles(element: HTMLElement, blockStyle: CSSProperties) {
  element.style.margin = '0';
  if (blockStyle.fontSize != null) {
    element.style.fontSize = `${blockStyle.fontSize}px`;
  }
  if (blockStyle.color) {
    element.style.color = String(blockStyle.color);
  }
  if (blockStyle.fontFamily) {
    element.style.fontFamily = String(blockStyle.fontFamily);
  }
  if (blockStyle.fontWeight != null) {
    element.style.fontWeight = String(blockStyle.fontWeight);
  }
  if (blockStyle.lineHeight != null) {
    element.style.lineHeight = String(blockStyle.lineHeight);
  }
}

function createExtensions(): Extensions {
  return [
    StarterKit.configure({
      heading: false,
      blockquote: false,
      codeBlock: false,
      code: false,
      horizontalRule: false,
    }),
    Underline,
    TextStyleKit,
  ];
}

export function EmailBlockTipTap({
  blockId,
  content,
  blockType,
  style,
  disabled,
  onChange,
  onEditorReady,
}: EmailBlockTipTapProps) {
  const lastSyncedContent = useRef(content);
  const extensions = useMemo(() => createExtensions(), []);

  const normalizeTitleDocument = (currentEditor: Editor) => {
    const { doc } = currentEditor.state;
    if (doc.childCount <= 1) {
      return;
    }

    const firstBlock = doc.firstChild;
    if (!firstBlock) {
      return;
    }

    currentEditor.commands.setContent(
      {
        type: 'doc',
        content: [firstBlock.toJSON()],
      },
      { emitUpdate: false }
    );
  };

  const editor = useEditor({
    extensions,
    content: blockTextToTipTapContent(content, blockType),
    editable: !disabled,
    autofocus: false,
    editorProps: {
      handleKeyDown: (_view, event) => {
        if (blockType === 'title' && event.key === 'Enter') {
          event.preventDefault();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      if (blockType === 'title') {
        normalizeTitleDocument(currentEditor);
      }

      const html = tipTapHtmlToBlockText(currentEditor.getHTML(), blockType);
      lastSyncedContent.current = html;
      onChange(html);
    },
  });

  useEffect(() => {
    lastSyncedContent.current = content;
  }, [blockId]);

  useEffect(() => {
    if (!editor) {
      return;
    }
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor || blockType !== 'title') {
      return;
    }

    normalizeTitleDocument(editor);

    const html = editor.getHTML();
    const normalized = blockTextToTipTapContent(tipTapHtmlToBlockText(html, blockType), blockType);
    if (html !== normalized) {
      editor.commands.setContent(normalized, { emitUpdate: false });
    }
  }, [editor, blockType]);

  useEffect(() => {
    if (!editor || content === lastSyncedContent.current) {
      return;
    }
    lastSyncedContent.current = content;
    editor.commands.setContent(blockTextToTipTapContent(content, blockType), { emitUpdate: false });
  }, [editor, content, blockType]);

  useEffect(() => {
    if (!editor) {
      return;
    }
    applyEditorStyles(editor.view.dom as HTMLElement, style);
  }, [editor, style]);

  useEffect(() => {
    if (!editor || !onEditorReady) {
      return undefined;
    }

    onEditorReady(editor);
    return () => onEditorReady(null);
  }, [editor, onEditorReady]);

  return (
    <div className={`ee-tiptap ee-tiptap--${blockType}`}>
      <EditorContent editor={editor} />
    </div>
  );
}
